import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import ReservationCard from './ReservationCard';
import LoadingSpinner from '../common/LoadingSpinner';
import Button from '../common/Button';

const MyReservations = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [reservations, setReservations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('active');
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setCurrentPage(1); // Reset to first page when filter changes
    loadReservations(1, true); // true = reset
  }, [filter]);

  const loadReservations = async (page = 1, reset = false) => {
    try {
      if (reset) {
        setIsLoading(true);
        setReservations([]);
      } else {
        setIsLoadingMore(true);
      }
      setError(null);

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/reservations/my-reservations?status=${filter}&page=${page}&limit=20`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        // Handle different types of errors
        if (response.status === 401) {
          throw new Error('Please log in again to view your reservations');
        } else if (response.status === 403) {
          throw new Error('You do not have permission to view reservations');
        } else if (response.status >= 500) {
          throw new Error('Server error - please try again later');
        } else {
          throw new Error('Failed to load reservations');
        }
      }

      const data = await response.json();

      // Handle new API response format
      let newReservations = Array.isArray(data.reservations) ? data.reservations : [];

      // For "active" (upcoming) reservations, sort in ascending order (soonest first)
      // For all other filters, keep descending order (most recent first)
      if (filter === 'active') {
        newReservations = [...newReservations].sort((a, b) =>
          new Date(a.start_time) - new Date(b.start_time)
        );
      }

      if (reset || page === 1) {
        setReservations(newReservations);
      } else {
        // Append to existing reservations for "Load More"
        setReservations(prev => [...prev, ...newReservations]);
      }

      setPagination(data.pagination);
      setCurrentPage(page);
    } catch (error) {
      console.error('Error loading reservations:', error);
      setError(error.message);
      if (reset) {
        setReservations([]); // Clear reservations on error only if this was a reset
      }
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  const handleReservationUpdate = () => {
    loadReservations(1, true); // Refresh the list from beginning
  };

  const filterOptions = [
    { value: 'active', label: 'Upcoming' },
    { value: 'checked_in', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'all', label: 'All' }
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="large" text="Loading your reservations..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <div className="flex items-center mb-2">
              <div className="w-1 h-10 bg-gradient-to-b from-teal-500 to-purple-500 mr-4 rounded"></div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-teal-600 via-purple-600 to-magenta-600 bg-clip-text text-transparent">
                My Reservations
              </h1>
            </div>
            <p className="ml-5 mt-2 text-base text-slate-600 font-medium">
              Manage your climbing board reservations
            </p>
          </div>

          {/* Filter Tabs */}
          <div className="mb-6">
            <div className="bg-white rounded-xl border-2 border-slate-900 shadow-retro p-2">
              <nav className="flex space-x-2">
                {filterOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setFilter(option.value)}
                    className={`flex-1 py-2 px-4 rounded-lg font-semibold text-sm transition-all ${
                      filter === option.value
                        ? 'bg-teal-500 text-white border-2 border-slate-900 shadow-retro'
                        : 'text-slate-600 hover:bg-teal-50 hover:text-teal-700 border-2 border-transparent'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {error && (
            <div className="mb-6 bg-coral-50 border-2 border-coral-500 rounded-xl p-6 shadow-retro">
              <div className="flex">
                <svg className="h-6 w-6 text-coral-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <div className="ml-3">
                  <h3 className="text-base font-bold text-coral-900">Unable to load reservations</h3>
                  <p className="mt-2 text-sm text-coral-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Reservations List */}
          {reservations.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border-2 border-slate-900 shadow-retro p-8">
              <svg className="mx-auto h-16 w-16 text-slate-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <h3 className="mt-4 text-lg font-bold text-slate-900">No reservations found</h3>
              <p className="mt-2 text-base text-slate-600">
                {filter === 'active'
                  ? 'You have no upcoming reservations.'
                  : filter === 'checked_in'
                    ? 'No completed reservations found.'
                    : `No ${filter} reservations found.`}
              </p>
              <div className="mt-6">
                <Button onClick={() => window.location.href = '/dashboard'}>
                  Make a Reservation
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {reservations.map((reservation) => (
                <ReservationCard
                  key={reservation.id}
                  reservation={reservation}
                  onUpdate={handleReservationUpdate}
                />
              ))}
              
              {/* Load More Button */}
              {pagination && pagination.hasMore && (
                <div className="text-center pt-6">
                  <Button
                    onClick={() => loadReservations(currentPage + 1, false)}
                    variant="outline"
                    loading={isLoadingMore}
                    disabled={isLoadingMore}
                  >
                    {isLoadingMore ? 'Loading...' : 'Load More Reservations'}
                  </Button>
                  <p className="text-sm text-slate-600 font-medium mt-3">
                    Showing {reservations.length} of {pagination.total} reservations
                  </p>
                </div>
              )}

              {/* End of Results */}
              {pagination && !pagination.hasMore && pagination.total > 0 && (
                <div className="text-center pt-6 bg-white rounded-xl border-2 border-slate-200 p-6">
                  <p className="text-sm text-slate-600 font-medium">
                    You've reached the end! Showing all {pagination.total} reservations from the last 30 days.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyReservations;